"use client";

import Flex from "@/components/core/flex";
import SectionTitle from "@/components/shared/sectionTitle";
import Text from "@/components/shared/text";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import useLocalizer from "@/hooks/useLocalizer";
import { cn, getFilePath } from "@/lib/utils/utils";
import { QuoteIcon } from "lucide-react";
import Image from "next/image";
import { TestimonialSection } from "@/types/home";
interface Props {
  className?: string;
  data?: TestimonialSection;
}
import dayjs from "dayjs";
const TestimonialContent = ({ data, className }: Props) => {
  const { isRtl } = useLocalizer();
  const testimonialSection = data;
  return (
    <section
      style={{
        backgroundImage: testimonialSection?.banner
          ? `url('${getFilePath(testimonialSection?.banner?.key ?? "")}')`
          : undefined,
      }}
      className={cn(
        "relative w-full pt-28 pb-36  min-h-[659px] mb-20 flex bg-cover bg-center bg-fixed bg-no-repeat bg-blend-overlay items-center",
        className,
        "bg-secondary/80",
      )}
    >
      <div className="w-full h-full grid grid-cols-1 xl:grid-cols-3 px-10 gap-4">
        <div className="col-span-1 flex flex-col justify-center gap-8 h-full">
          <SectionTitle
            title={
              isRtl
                ? (testimonialSection?.arTitle ?? "")
                : (testimonialSection?.enTitle ?? "")
            }
            titleClassName="text-muted!"
            description={
              isRtl
                ? (testimonialSection?.arSubTitle ?? "")
                : testimonialSection?.enSubTitle
            }
            descriptionClassName="text-white"
            lineStyle="2"
          />
          <Text className="text-muted/80">
            {isRtl
              ? testimonialSection?.arDescription
              : testimonialSection?.enDescription}
          </Text>
        </div>
        <div className="xl:col-span-2 my-5">
          <Carousel>
            <CarouselContent dir="ltr">
              {testimonialSection?.reviews?.map((review) => (
                <CarouselItem
                  key={review.id}
                  className="basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <article
                    dir={isRtl ? "rtl" : "ltr"}
                    className={cn(
                      "relative flex flex-col justify-between gap-4 bg-white pb-8 px-8 pt-4 border border-border/50 w-full rounded-lg h-85",
                      isRtl ? "rounded-tr-none" : "rounded-tl-none",
                    )}
                  >
                    <Flex direction="col" spacing={{ all: 4 }}>
                      <Flex
                        center
                        className={cn(
                          "size-10 bg-primary/30 text-primary rounded-lg ",
                          isRtl ? "rounded-tl-none" : "rounded-tr-none",
                        )}
                      >
                        <QuoteIcon size={24} />
                      </Flex>
                      <Text className="text-primary text-balance lg:text-lg font-medium line-clamp-4">
                        {isRtl ? review.arPost : review.enPost}
                      </Text>
                      <Separator />
                    </Flex>
                    <Flex direction="row" items="center" spacing={{ all: 4 }}>
                      {review?.image && (
                        <div className="relative w-20 h-20 rounded-full overflow-hidden">
                          <Image
                            src={getFilePath(review?.image?.key ?? "")}
                            alt={isRtl ? review?.arName : review?.enName}
                            fill
                            priority
                            placeholder="empty"
                            className=" rounded-full object-cover absolute top-0 left-0"
                          />
                        </div>
                      )}
                      <Flex direction="col" spacing={{ all: 2 }}>
                        <Text as="span" className="text-secondary font-bold">
                          {isRtl ? review?.arName : review?.enName}
                        </Text>
                        <Text as="span" className="text-primary font-medium">
                          {isRtl ? review?.arSubTitle : review?.enSubTitle}
                        </Text>
                        {review?.postAt && (
                          <Text
                            bdi
                            as="span"
                            className="text-secondary font-medium"
                          >
                            {dayjs(review?.postAt).format("YYYY-MM-DD")}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default TestimonialContent;
